import { useState, useEffect } from "react";
import { useTheme } from "../../../theme";
import { BRAND_COLORS } from "../../../theme/colors.js";
import { SOCIALS } from "../../../data/socials.js";

export default function FloatingSocials({ ready = false }) {
  const t = useTheme();
  const [visible, setVisible] = useState(false);
  const [hover, setHover] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!ready) return;
    const timeout = setTimeout(() => setVisible(true), 4200);
    return () => clearTimeout(timeout);
  }, [ready]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const isRotated = scrolled || hover;
  const barColor = hover
    ? BRAND_COLORS.cyan
    : scrolled
      ? BRAND_COLORS.orange
      : BRAND_COLORS.purple;

  return (
    <div
      onMouseEnter={() => { setHover(true); setExpanded(true); }}
      onMouseLeave={() => { setHover(false); setExpanded(false); }}
      style={{
        position: "fixed",
        right: visible ? 0 : -200,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 90,
        display: "flex",
        flexDirection: isRotated ? "row" : "column",
        alignItems: "center",
        gap: 0,
        borderRadius: isRotated ? "10px 0 0 10px" : "10px 0 0 10px",
        background: hover
          ? `${barColor}22`
          : scrolled
            ? t.navBg
            : t.overlayBg,
        border: `1px solid ${hover ? barColor : scrolled ? `${barColor}44` : t.borderStrong}`,
        borderRight: "none",
        backdropFilter: "blur(20px)",
        padding: expanded ? (isRotated ? "10px 16px" : "16px 10px") : (isRotated ? "10px 14px" : "14px 10px"),
        transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
        boxShadow: hover
          ? `0 0 30px ${barColor}33, inset 0 0 20px ${barColor}11`
          : scrolled
            ? `0 0 20px ${barColor}18`
            : "0 4px 20px rgba(0,0,0,0.3)",
        overflow: "hidden",
      }}
    >
      {/* Collapsed: show single icon indicator */}
      {!expanded && (
        <div style={{
          display: "flex",
          flexDirection: isRotated ? "row" : "column",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
        }}>
          <span style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 12, color: barColor,
            transition: "color 0.3s",
          }}>⋯</span>
        </div>
      )}

      {/* Expanded: show all social icons */}
      {expanded && SOCIALS.map((s, i) => (
        <SocialIcon key={s.name} social={s} color={barColor} index={i} isRotated={isRotated} />
      ))}
    </div>
  );
}

function SocialIcon({ social, color, index, isRotated }) {
  const t = useTheme();
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={social.href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32,
        borderRadius: 6,
        background: hovered ? `${color}25` : "transparent",
        color: hovered ? t.text : color,
        fontFamily: "'DM Mono', monospace",
        fontSize: social.icon === "in" ? 11 : 14,
        fontWeight: social.icon === "in" ? 700 : 400,
        textDecoration: "none",
        cursor: "pointer",
        transition: "all 0.2s ease",
        opacity: 1,
        animation: `fadeIn 0.3s ease ${index * 0.06}s both`,
        textShadow: hovered ? `0 0 10px ${color}66` : "none",
      }}
      title={social.name}
    >
      {social.icon}
    </a>
  );
}
